import { AccessDeniedScreen } from '@slims-pro/ui/organisms';
import { Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { useMe } from '@/hooks/use-me';
import { useMeta } from '@/hooks/use-meta';
import { EXT_ROUTES } from '@/modules/ext-routes';
import { MODULE_ROUTES, type ModuleId } from '@/modules/registry';
import { ProShell } from '@/routes/pro-shell';
import { ProtectedRoute } from '@/routes/protected-route';
import { RequireExtension } from '@/routes/require-extension';
import { RequireModule } from '@/routes/require-module';
import { RootLayout } from '@/routes/root-layout';
import { RouteError } from '@/routes/route-error';

/** `/` → dashboard of `meta.defaultMode`, or the first enabled and permitted module. */
function HomeRedirect() {
	const meta = useMeta().data!;
	const permitted = new Set(useMe().data!.permissions.map((p) => p.module));
	const target = permitted.has(meta.defaultMode)
		? meta.defaultMode
		: meta.modules.find((id) => permitted.has(id));
	if (!target) return <AccessDeniedScreen actionLabel="Ricarica" actionHref="/" />;
	return <Navigate to={`/${target}`} replace />;
}

const moduleIds = Object.keys(MODULE_ROUTES) as ModuleId[];

// `handle.title` feeds the browser tab title (`useDocumentTitle`).
export const router = createBrowserRouter([
	{
		element: <RootLayout />,
		errorElement: <RouteError />,
		children: [
			{
				element: <ProtectedRoute />,
				children: [
					{
						element: <ProShell />,
						children: [
							{ index: true, element: <HomeRedirect /> },
							...moduleIds.map((id) => {
								const { path, title, Page, feature } = MODULE_ROUTES[id];
								return {
									path,
									element: (
										<RequireModule id={id} feature={feature}>
											<Suspense fallback={null}>
												<Page />
											</Suspense>
										</RequireModule>
									),
									handle: { title },
								};
							}),
							// Client extension routes (ADR-013 §4); pages may be lazy.
							...EXT_ROUTES.map(({ path, title, feature, module, Page }) => ({
								path: `x/${path}`,
								element: (
									<RequireExtension feature={feature} module={module}>
										<Suspense fallback={null}>
											<Page />
										</Suspense>
									</RequireExtension>
								),
								handle: { title },
							})),
							{ path: '*', element: <Navigate to="/" replace /> },
						],
					},
				],
			},
		],
	},
]);
